import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";

type AuditEvent = {
  id?: string;
  createdAt?: string;
  app?: string;
  module?: string;
  action?: string;
  description?: string;
  actorName?: string | null;
};

type AuditResponse = { events?: AuditEvent[]; total?: number };

export function SettingsAuditPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<AuditResponse>("/api/audit?limit=100");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load audit log.");
        setEvents([]);
      } else {
        setError(null);
        setEvents(res.data?.events ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Audit log</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Recent org events from <code className="text-sm">/api/audit</code>.
        </p>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading audit events…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {!loading && !error ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">App</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No audit events returned.
                  </td>
                </tr>
              ) : (
                events.map((ev) => (
                  <tr key={ev.id || `${ev.createdAt}-${ev.description}`} className="border-b border-slate-100 dark:border-white/5">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600 dark:text-slate-300">
                      {ev.createdAt ? new Date(ev.createdAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {[ev.app, ev.module].filter(Boolean).join(" / ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{ev.action || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                      {ev.description || "—"}
                      {ev.actorName ? (
                        <span className="mt-0.5 block text-xs text-slate-500">by {ev.actorName}</span>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <Link to="/settings" className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-300">
        ← Settings overview
      </Link>
    </div>
  );
}
